import { LabeledValue } from 'antd/lib/select'
import CIMSSelect from '../../..'
import { CommonSelect, SEPARATOR } from '../../types'
import { useRecipientSelectContext } from './RecipientSelectContext'
import { useState } from 'react'

/**
 * Regex to test the validity of an email address. Copied from the HTML spec for
 * email input types.
 *
 * @see https://html.spec.whatwg.org/multipage/input.html#email-state-(type=email)
 */
const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

/**
 * A complex {@link CIMSSelect} component for users, non user emails,
 * and entering arbitrary email addresses.
 *
 * Wrap anything using this component in the RecipientSelectProvider
 *
 * This is designed to work with custom workflows and was forked from
 * `RecipientsSelect` in order to support entering arbitrary email addresses.
 */
const RecipientSelect = (
  props: CommonSelect<'virtual' | 'mode' | 'tokenSeparators'>
) => {
  const {
    options,
    loading,
    findOptionFromAddress,
    findAddressFromValue,
    findOptionFromValue,
  } = useRecipientSelectContext()
  const [value, setValue] = useState<LabeledValue[]>([])

  return (
    <CIMSSelect
      mode="tags"
      autoClearSearchValue={true}
      tokenSeparators={[',']}
      options={options}
      loading={loading}
      virtual={false}
      {...props}
      // Keep `onChange` after `{...props}` so this handler is not overridden.
      // `Form.Item` injects its own `onChange` via props, and we need this
      // handler to normalize recipient values before forwarding them.
      onChange={(value) => {
        const seen = new Set<string>()
        const values = (value as LabeledValue[]).flatMap((item) => {
          const raw = item.value.toString()

          // If item value contains SEPARATOR, it suggests it wasn't manually
          // entered.
          if (raw.includes(SEPARATOR)) {
            const addr = findAddressFromValue(raw)
            if (addr) seen.add(addr.toLowerCase())
            return [item]
          }

          // Ignore invalid emails.
          if (!EMAIL_REGEX.test(raw)) return []

          // Drop duplicates.
          const normalized = raw.toLowerCase()
          if (seen.has(normalized)) return []

          // Add and transform into option, if available.
          seen.add(normalized)
          const option = findOptionFromAddress(raw)
          return [option?.value ? { ...option, value: option.value } : item]
        })

        const options = values.map(
          ({ value }) => findOptionFromValue(value) ?? {}
        )

        setValue(values)
        // TS will complain here because `options` will include a `{}` if an
        // option can't be found from the value, which is not a
        // `DefaultOptionType`. Ant design does the same thing when the onChange
        // handler isn't overridden. It appears Ant Design's `onChange` typing
        // is inaccurate for the option param.
        // @ts-expect-error
        props.onChange?.(values, options)
      }}
      value={props.value ?? value}
    />
  )
}

export default RecipientSelect
